/**
 * EVO — avtomatik qayta zakaz (replenishment) ma'lumot qatlami.
 *
 * Tiplar `apps/api/src/modules/replenishment/reorder.service.ts` dagi javob
 * interfeyslaridan MAYDONMA-MAYDON ko'chirilgan (`lib/procurement.ts` bilan
 * bir xil naqsh). Bu qo'lda saqlanadigan shartnoma: server ustunni qayta
 * nomlasa TypeScript uni ushlamaydi, shuning uchun har o'zgarish jonli
 * tekshiriladi.
 *
 * ============================================================================
 * MIQDORLAR — `string`, VA BU YERDA AYNIQSA MUHIM
 * ============================================================================
 * `parseFloat` taqiqlangan (`lib/format.ts` fayl boshidagi qoida). Bu ekranda
 * sabab boshqa ekranlardagidan ham og'irroq: bu yerdagi raqam 3–4 oylik va
 * naqd to'lanadigan buyurtmaga aylanadi. Oxirgi xonadagi xato 5 000 donalik
 * partiyada bir necha yuz dona bo'lib chiqadi.
 *
 * ============================================================================
 * NIMA UCHUN «HISOBOT» EMAS, ALOHIDA QATLAM
 * ============================================================================
 * `/reports/reorder` bilan formula BIR XIL (server tomonda parity testi bor),
 * lekin bu yerda natija SAQLANADI va hujjatga aylanadi: tahlil → ko'rib
 * chiqish → qoralama xarid buyurtmasi. Hisobot esa faqat ko'rsatadi va yopiladi.
 * Shuning uchun `lib/reports.ts` ga qo'shilmadi — u yerda `Meta` va eksport
 * karkasi bor, bu yerda esa holat o'zgarishi va audit izi bor.
 */
import { api } from './api';
import type { Page, SortDir } from './products';

/** `reorder.dto.ts` → `REORDER_RUN_STATUS_VALUES`. */
export const REORDER_RUN_STATUSES = ['draft', 'reviewed', 'ordered', 'cancelled'] as const;
export type ReorderRunStatus = (typeof REORDER_RUN_STATUSES)[number];

/** `reorder.calc.ts` → `ReorderLineStatus`. `/reports/reorder` bilan bir xil lug'at. */
export const REORDER_LINE_STATUSES = ['no_demand', 'order_now', 'ok'] as const;
export type ReorderLineStatus = (typeof REORDER_LINE_STATUSES)[number];

/**
 * Satr bayroqlari — `reorder.calc.ts` → `lineFlags()`.
 *
 * Ro'yxat SHU YERDA takrorlanadi, chunki har bayroqning ekranda o'z rangi va
 * o'z izohi bor. Server yangi bayroq qo'shsa u shu yerda ko'rinmaydi va
 * `FLAG_LABELS` dagi `Record` to'liq bo'lmagani uchun **kompilyatsiya
 * yiqiladi** — ya'ni yangi bayroq jimgina e'tibordan chetda qolmaydi.
 */
export const REORDER_LINE_FLAGS = [
  'negative_stock',
  'no_supplier',
  'no_price',
  'above_formula',
  'below_formula',
  'draft_po',
] as const;
export type ReorderLineFlag = (typeof REORDER_LINE_FLAGS)[number];

/** `reorder.calc.ts` → `ReorderParams`. Tahlil bilan BIRGA saqlanadi. */
export interface ReorderParams {
  readonly lookbackDays: number;
  readonly coverDays: number;
  readonly safetyDays: number;
  readonly includeInTransit: boolean;
}

export interface ReorderRunHeader {
  readonly id: number;
  readonly docNo: string;
  readonly categoryId: number | null;
  readonly categoryName: string | null;
  readonly runDate: string;
  /**
   * Tahlil QAYSI farazlar bilan hisoblangani — natija bilan BIRGA saqlangan.
   *
   * Kategoriya sozlamasi keyin o'zgarsa, eski tahlil baribir o'z parametrlari
   * bilan o'qiladi. Parametrni sozlamadan qayta o'qish «kecha nima uchun
   * shunday chiqqan?» degan savolga NOTO'G'RI javob berardi.
   */
  readonly params: ReorderParams;
  readonly status: ReorderRunStatus;
  readonly lineCount: number;
  readonly suggestedCount: number;
  readonly purchaseOrderCount: number;
  readonly notes: string | null;
  readonly reviewedAt: string | null;
  readonly reviewedBy: number | null;
  readonly orderedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ReorderRunLine {
  readonly id: number;
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  readonly onHandQty: string;
  /** Ochiq (tasdiqlangan) xarid buyurtmalarining qolgan qismi. */
  readonly inTransitQty: string;
  readonly reservedQty: string;
  readonly soldQty: string;
  readonly dailyAvgQty: string;
  readonly leadTimeDays: number;
  readonly targetQty: string;
  readonly suggestedQty: string;
  readonly supplierId: number | null;
  readonly supplierName: string | null;
  readonly isRejected: boolean;
  readonly rejectReason: string | null;
  readonly purchaseOrderId: number | null;
  /** Hali TASDIQLANMAGAN qoralamalardagi miqdor. Formulaga kirmaydi. */
  readonly draftPoQty: string;
  readonly status: ReorderLineStatus;
  readonly flags: readonly string[];
}

export interface ReorderRunDetail extends ReorderRunHeader {
  readonly lines: readonly ReorderRunLine[];
}

export interface ReorderRunListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly sortBy?: string | undefined;
  readonly sortDir: SortDir;
  readonly status?: ReorderRunStatus | undefined;
  readonly categoryId?: number | undefined;
}

export interface CreateReorderRunBody {
  readonly categoryId?: number;
  readonly lookbackDays?: number;
  readonly coverDays?: number;
  readonly safetyDays?: number;
  readonly includeInTransit?: boolean;
  readonly notes?: string | null;
}

export interface UpdateReorderLineBody {
  readonly suggestedQty?: string;
  readonly isRejected?: boolean;
  readonly rejectReason?: string | null;
  readonly supplierId?: number | null;
}

/** `reorder-orders.service.ts` → `CreateReorderOrdersResult`. */
export interface CreateReorderOrdersResult {
  readonly runId: number;
  readonly purchaseOrderCount: number;
  readonly orders: readonly {
    readonly purchaseOrderId: number;
    readonly docNo: string;
    readonly supplierId: number;
    readonly supplierName: string;
    readonly currencyCode: string;
    readonly expectedDate: string;
    readonly lineCount: number;
    readonly missingPriceCount: number;
  }[];
  readonly skipped: {
    readonly rejected: number;
    readonly noSupplier: number;
    readonly zeroQty: number;
  };
  readonly run: ReorderRunDetail;
}

/**
 * Navbati kelgan kategoriyalar — `dryRun` bilan hech narsa yozilmaydi.
 *
 * Ekran HAR DOIM avval `dryRun: true` bilan so'raydi va odamga «bugun mana
 * shu kategoriyalar tahlil qilinadi» deb ko'rsatadi. Sabab: bu amal bir
 * necha kategoriya bo'yicha bir vaqtda hujjat yaratadi va uni «bosdim,
 * nima bo'lganini bilmayman» holatiga qoldirib bo'lmaydi.
 */
export interface RunDueCandidate {
  readonly categoryId: number;
  readonly code: string;
  readonly name: string;
  readonly reviewEveryDays: number;
  readonly lastRunDate: string | null;
  readonly isDue: boolean;
}

export interface RunDueResult {
  readonly asOf: string;
  readonly dryRun: boolean;
  readonly dueCount: number;
  readonly candidates: readonly RunDueCandidate[];
  /** Faqat `dryRun: false` da keladi. */
  readonly createdCount?: number;
  readonly created?: readonly { readonly categoryId: number; readonly runId: number }[];
  /** `reason`: `already_run_today` | `failed`. */
  readonly skipped?: readonly { readonly categoryId: number; readonly reason: string }[];
}

export const reorderKeys = {
  all: ['reorder'] as const,
  list: (params: ReorderRunListParams) => ['reorder', 'list', params] as const,
  detail: (id: number) => ['reorder', 'detail', id] as const,
  due: () => ['reorder', 'due'] as const,
};

function toQuery(params: ReorderRunListParams): string {
  const search = new URLSearchParams();
  search.set('page', String(params.page));
  search.set('pageSize', String(params.pageSize));
  if (params.sortBy) search.set('sortBy', params.sortBy);
  search.set('sortDir', params.sortDir);
  if (params.status) search.set('status', params.status);
  if (params.categoryId !== undefined) search.set('categoryId', String(params.categoryId));
  return search.toString();
}

export function fetchReorderRuns(
  params: ReorderRunListParams,
  signal?: AbortSignal,
): Promise<Page<ReorderRunHeader>> {
  return api.get<Page<ReorderRunHeader>>(`/reorder/runs?${toQuery(params)}`, { signal });
}

export function fetchReorderRun(id: number, signal?: AbortSignal): Promise<ReorderRunDetail> {
  return api.get<ReorderRunDetail>(`/reorder/runs/${id}`, { signal });
}

export function createReorderRun(body: CreateReorderRunBody): Promise<ReorderRunDetail> {
  return api.post<ReorderRunDetail>('/reorder/runs', body);
}

export function runDueReorder(dryRun: boolean): Promise<RunDueResult> {
  return api.post<RunDueResult>('/reorder/runs/due', { dryRun });
}

export function updateReorderLine(
  runId: number,
  lineId: number,
  body: UpdateReorderLineBody,
): Promise<ReorderRunDetail> {
  return api.put<ReorderRunDetail>(`/reorder/runs/${runId}/lines/${lineId}`, body);
}

export function reviewReorderRun(runId: number, notes?: string | null): Promise<ReorderRunDetail> {
  return api.post<ReorderRunDetail>(`/reorder/runs/${runId}/review`, { notes: notes ?? null });
}

export function createReorderOrders(runId: number): Promise<CreateReorderOrdersResult> {
  return api.post<CreateReorderOrdersResult>(`/reorder/runs/${runId}/create-orders`, {});
}
